import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Spinner, MapPin, CheckCircle } from "@phosphor-icons/react";
import { IndustryBubbleBar } from "@/components/IndustryBubbles";
import { toast } from "sonner";

export default function IndustrySettings({ company, onChanged }) {
  const [list, setList] = useState([]);
  const [locations, setLocations] = useState([]);
  const [types, setTypes] = useState({});
  const [pick, setPick] = useState(company?.industry || "");
  const [addLocs, setAddLocs] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/industries").then(({ data }) => setList(data)).catch(() => {});
    api.get("/locations").then(({ data }) => setLocations(data)).catch(() => {});
    api.get("/asset-types").then(({ data }) => setTypes(data)).catch(() => {});
  };
  useEffect(load, []);

  const target = list.find((i) => i.key === pick);
  const have = new Set(locations.map((l) => l.name.trim().toLowerCase()));
  const newLocs = target ? target.locations.filter((l) => !have.has(l.toLowerCase())) : [];
  const newTypes = target ? target.asset_types.filter((t) => !types[t.key]) : [];
  const unchanged = pick === (company?.industry || "");

  const apply = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/company/industry", { industry: pick, add_locations: addLocs });
      toast.success(`Switched to ${target.label}${data.added_locations.length ? ` · ${data.added_locations.length} locations added` : ""}`);
      onChanged?.(data.industry); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not switch industry"); }
    setSaving(false);
  };

  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-6 space-y-5" data-testid="industry-settings">
      <div>
        <Label className="text-sm font-semibold">Industry</Label>
        <p className="text-xs text-zinc-500 mt-0.5">Current: <b data-testid="industry-current">{list.find((i) => i.key === company?.industry)?.label || "Not set (all asset types shown)"}</b>. Switching adds that industry's asset types and locations — nothing you already have is removed.</p>
      </div>
      <IndustryBubbleBar list={list} selected={pick} onSelect={(k) => setPick(k || pick)} />
      {target && !unchanged && (
        <div className="rounded-lg bg-zinc-50 border border-zinc-100 p-4 space-y-3 text-sm" data-testid="industry-switch-preview">
          <p className="font-semibold text-zinc-900">What switching to {target.label} adds</p>
          <div>
            <p className="text-xs uppercase tracking-wider font-semibold text-zinc-500 mb-1">Asset types</p>
            {newTypes.length ? <div className="flex flex-wrap gap-1.5">{newTypes.map((t) => <span key={t.key} className="inline-flex items-center gap-1 rounded-md bg-white border border-zinc-200 px-2 py-0.5 text-xs font-medium" data-testid={`industry-new-type-${t.key}`}><CheckCircle size={11} weight="fill" className="text-emerald-500" />{t.label}</span>)}</div> : <p className="text-xs text-zinc-500">All of this industry's types are already available.</p>}
          </div>
          <div>
            <label className="flex items-center gap-2 cursor-pointer text-sm"><Checkbox checked={addLocs} onCheckedChange={(v) => setAddLocs(!!v)} data-testid="industry-add-locations" />Also add {target.label} locations</label>
            {addLocs && (newLocs.length ? <div className="flex flex-wrap gap-1.5 mt-2">{newLocs.map((l) => <span key={l} className="inline-flex items-center gap-1 rounded-md bg-emerald-50 text-emerald-800 border border-emerald-100 px-2 py-0.5 text-xs font-medium"><MapPin size={11} weight="fill" />{l}</span>)}</div> : <p className="text-xs text-zinc-500 mt-1">You already have all of these locations.</p>)}
          </div>
        </div>
      )}
      <Button onClick={apply} disabled={saving || !target || unchanged} className="bg-zinc-900 hover:bg-zinc-800 text-white font-semibold" data-testid="industry-switch-btn">
        {saving ? <Spinner size={18} className="animate-spin" /> : unchanged ? "This is your current industry" : `Switch to ${target?.label || "…"}`}
      </Button>
    </div>
  );
}
